// Reine Kennzahl-Mathematik der Chef-Übersicht aus der Auftragsliste (OrderListItemDTO). I/O-frei und
// getestet. Geld in ganzen Cent. Überfällig kommt aus @wf/shared (EINE Wahrheit, kein vierter lokaler
// isOverdue-Klon). „Auslastung %" fehlt bewusst — die braucht das Zeiterfassungs-Backend (W9) und wird
// erst dann ehrlich berechenbar; bis dahin zeigt die UI nur echte Auftrags-Zahlen.
import { isDoneToday, isOverdue, ORDER_STATUSES, parseDbTimestamp, type OrderListItemDTO } from '@wf/shared'

const STATUS_SET = new Set<string>(ORDER_STATUSES)
const COMPLETED = new Set<string>(['fertig', 'abgeholt'])

/** Auftragswert = Σ Teile + Σ Positionen (ganze Cent; Arbeitslohn ist eine Position) — wie die Auftragskarte rechnet. */
export function orderValueCents(o: Pick<OrderListItemDTO, 'partsCents' | 'itemsCents'>): number {
  return o.partsCents + o.itemsCents
}

export interface OverviewKpis {
  /** Aufträge, die noch nicht abgeholt sind. */
  activeCount: number
  /** Σ Wert der nicht-abgeholten Aufträge (offener Auftragswert). */
  openValueCents: number
  /** Heute fertiggemeldet (status 'fertig' & finishedAt fällt auf heute). */
  doneTodayCount: number
  /** Aktuell abholbereit (status 'fertig', unabhängig vom Datum). */
  readyCount: number
  /** Kostenvoranschlag versendet, wartet auf Kundenentscheidung. */
  estimateOpenCount: number
  /** Termin überschritten und nicht fertig/abgeholt. */
  overdueCount: number
  /** Priorität hoch und nicht fertig/abgeholt. */
  urgentCount: number
  /** Anzahl je Auftragsstatus (für das Status-Board). */
  statusCounts: Record<string, number>
}

export function computeKpis(orders: readonly OrderListItemDTO[], today: string, nowMs: number): OverviewKpis {
  const statusCounts: Record<string, number> = {}
  for (const s of ORDER_STATUSES) statusCounts[s] = 0

  let activeCount = 0
  let openValueCents = 0
  let doneTodayCount = 0
  let readyCount = 0
  let estimateOpenCount = 0
  let overdueCount = 0
  let urgentCount = 0

  for (const o of orders) {
    if (STATUS_SET.has(o.status)) statusCounts[o.status] = (statusCounts[o.status] ?? 0) + 1
    if (o.status !== 'abgeholt') {
      activeCount += 1
      openValueCents += orderValueCents(o)
    }
    if (o.status === 'fertig') {
      readyCount += 1
      // „Heute fertig" nach LOKALEM Kalendertag (Europe/Berlin) über den geteilten Helfer — NICHT per
      // UTC-Präfix-Vergleich: finishedAt ist ein UTC-Zeitstempel, slice(0,10) läge nahe Mitternacht daneben
      // (dokumentierte UTC-Datums-Falle, CLAUDE.md §3). isDoneToday vergleicht localDayKey(ms) == localDayKey(now).
      const finishedMs = parseDbTimestamp(o.finishedAt)
      if (!Number.isNaN(finishedMs) && isDoneToday(finishedMs, nowMs)) doneTodayCount += 1
    }
    if (o.estimateStatus === 'sent') estimateOpenCount += 1
    if (isOverdue(o, today)) overdueCount += 1
    if (o.prio === 'hoch' && !COMPLETED.has(o.status)) urgentCount += 1
  }

  return {
    activeCount,
    openValueCents,
    doneTodayCount,
    readyCount,
    estimateOpenCount,
    overdueCount,
    urgentCount,
    statusCounts,
  }
}

export interface MechanicLoad {
  mechanicId: number
  name: string
  /** Nicht-abgeholte Aufträge, die diesem Mechaniker zugewiesen sind. */
  activeCount: number
}

/**
 * Aktive Auftrags-Last je zugewiesenem Mechaniker (absteigend nach Anzahl, dann Name). Es erscheinen nur
 * Mechaniker, die tatsächlich aktive Aufträge tragen — ohne ein separates Mitarbeiter-Backend ist das die
 * ehrliche Datengrundlage. Die echte Auslastung in % (Soll/Ist) folgt mit dem Zeiterfassungs-Backend.
 */
export function mechanicLoads(orders: readonly OrderListItemDTO[]): MechanicLoad[] {
  const byId = new Map<number, MechanicLoad>()
  for (const o of orders) {
    if (o.status === 'abgeholt') continue
    if (o.mechanicId == null) continue
    const existing = byId.get(o.mechanicId)
    if (existing) {
      existing.activeCount += 1
    } else {
      byId.set(o.mechanicId, {
        mechanicId: o.mechanicId,
        name: o.mechanicName ?? 'Unbekannt',
        activeCount: 1,
      })
    }
  }
  return [...byId.values()].sort((a, b) => b.activeCount - a.activeCount || a.name.localeCompare(b.name))
}

/** Aktive Aufträge eines bestimmten Mechanikers (für die Mechaniker-Übersicht „Meine Arbeit"). */
export function myActiveOrders(orders: readonly OrderListItemDTO[], userId: number): OrderListItemDTO[] {
  return orders.filter((o) => o.mechanicId === userId && o.status !== 'abgeholt')
}

/** Σ offene (nicht erledigte) Aufgaben über eine Auftragsmenge. */
export function openTaskCount(orders: readonly OrderListItemDTO[]): number {
  return orders.reduce((sum, o) => sum + Math.max(0, o.taskTotal - o.taskDone), 0)
}
